import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import UserCard from '../user/UserCard';
import PostCard from '../posts/PostCard';
import CommunityCard from '../communities/CommunityCard';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import Button from '../common/Button';

/**
 * Enhanced SearchResults component for displaying search results with advanced features
 * @param {Object} props - Component props
 * @param {string} props.query - Search query
 * @param {Array} props.users - User results
 * @param {Array} props.posts - Post results
 * @param {Array} props.communities - Community results
 * @param {Array} props.trendingTopics - Trending topics
 * @param {Array} props.trendingUsers - Trending users
 * @param {boolean} props.loading - Whether results are loading
 * @param {Object} props.pagination - Pagination data
 * @param {string} props.activeType - Active result type
 * @param {Object} props.filters - Applied filters
 * @param {Function} props.onPageChange - Page change callback
 * @param {Function} props.onFilterChange - Filter change callback
 * @returns {JSX.Element} Enhanced SearchResults component
 */
const SearchResults = ({
  query = '',
  users = [],
  posts = [],
  communities = [],
  trendingTopics = [],
  trendingUsers = [],
  loading = false,
  pagination = { page: 1, pages: 1 },
  activeType = '',
  filters = {},
  onPageChange,
  onFilterChange,
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserActionMenu, setShowUserActionMenu] = useState(false);
  const userActionMenuRef = useRef(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    function handleOutsideClick(event) {
      if (userActionMenuRef.current && !userActionMenuRef.current.contains(event.target)) {
        setShowUserActionMenu(false);
      }
    }
    
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [userActionMenuRef]);
  
  // Get total results count
  const getTotalResults = () => {
    if (activeType === 'users') return users.length;
    if (activeType === 'posts') return posts.length;
    if (activeType === 'communities') return communities.length;
    return users.length + posts.length + communities.length;
  };
  
  // Handle user action menu
  const handleUserActionClick = (user, e) => {
    e.stopPropagation();
    setSelectedUser(user);
    setShowUserActionMenu(prev => !prev);
  };
  
  // Show active filters summary
  const renderActiveFilters = () => {
    const activeFilters = Object.entries(filters)
      .filter(([_, value]) => value)
      .map(([key, value]) => {
        // Format filter name for display
        const formatKey = (key) => {
          return key
            .replace(/([A-Z])/g, ' $1') // Insert space before capital letters
            .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
            .replace(/([a-z])([A-Z])/g, '$1 $2'); // Add space between camelCase
        };
        
        return (
          <div 
            key={key} 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2 mb-2"
          >
            <span>{formatKey(key)}: {value}</span>
            <button
              type="button"
              onClick={() => onFilterChange && onFilterChange(key, '')}
              className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none"
            >
              <span className="sr-only">Remove filter {key}</span>
              <svg 
                className="h-3 w-3" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 20 20" 
                fill="currentColor" 
                aria-hidden="true"
              >
                <path 
                  fillRule="evenodd" 
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                  clipRule="evenodd" 
                />
              </svg>
            </button>
          </div>
        );
      });
    
    if (activeFilters.length === 0) return null;
    
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Active Filters</h3>
          <button
            type="button"
            onClick={() => onFilterChange && onFilterChange('reset')}
            className="text-sm text-blue-500 hover:text-blue-700"
          >
            Clear All
          </button>
        </div>
        <div className="flex flex-wrap">
          {activeFilters}
        </div>
      </div>
    );
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader size="lg" />
      </div>
    );
  }
  
  // Show empty state when no results
  if (query && getTotalResults() === 0) {
    return (
      <div className="space-y-6">
        {renderActiveFilters()}
        
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center my-4">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">No results found</h3>
          <p className="mt-1 text-sm text-gray-500">
            We couldn't find anything matching "{query}".
            Try different keywords or check your spelling.
          </p>
          
          {/* Trending suggestions when no results */}
          {trendingTopics.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Try these trending searches instead:</h4>
              <div className="flex flex-wrap justify-center">
                {trendingTopics.slice(0, 5).map((topic, index) => (
                  <Link
                    key={index}
                    to={`/search?q=${encodeURIComponent(topic.tag || topic)}`}
                    className="inline-flex items-center px-3 py-1.5 m-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
                  >
                    {topic.tag || topic}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // When no search is performed yet, show trending content
  if (!query) {
    return (
      <div className="space-y-6">
        {/* Trending searches */}
        {trendingTopics.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <svg 
                className="h-5 w-5 mr-2 text-gray-500" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" 
                />
              </svg>
              <h2 className="text-lg font-medium text-gray-900">Trending Topics</h2>
            </div>
            
            <div className="flex flex-wrap">
              {trendingTopics.map((topic, index) => (
                <Link
                  key={index}
                  to={`/search?q=${encodeURIComponent(topic.tag || topic)}`}
                  className="inline-flex items-center px-3 py-1.5 m-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
                >
                  {topic.tag || topic}
                  {topic.count && <span className="ml-1.5 text-xs text-gray-500">({topic.count})</span>}
                </Link>
              ))}
            </div>
          </div>
        )}
        
        {/* Trending users */}
        {trendingUsers.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <svg 
                  className="h-5 w-5 mr-2 text-gray-500" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" 
                  />
                </svg>
                <h2 className="text-lg font-medium text-gray-900">Trending People</h2>
              </div>
            </div>
            
            <div className="divide-y divide-gray-200">
              {trendingUsers.map(user => (
                <div key={user._id} className="p-4">
                  <UserCard 
                    user={user} 
                    showFollowButton 
                    showMessageButton 
                  />
                </div>
              ))}
            </div>
            
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <Link 
                to="/search?type=users" 
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                View all people →
              </Link>
            </div>
          </div>
        )}
        
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center my-4">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Search for content</h3>
          <p className="mt-1 text-sm text-gray-500">
            Enter a search term to find people, posts, or communities.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Show active filters */}
      {renderActiveFilters()}
      
      {/* Search stats */}
      <div className="text-sm text-gray-600 mb-2">
        Found {pagination.total || getTotalResults()} results for "{query}"
      </div>
      
      {/* User results */}
      {(activeType === '' || activeType === 'users') && users.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">People</h2>
              {activeType === '' && users.length > 3 && (
                <Link 
                  to={`/search?q=${encodeURIComponent(query)}&type=users`}
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  View all
                </Link>
              )}
            </div>
          </div>
          
          <div className="divide-y divide-gray-200">
            {users.map((user) => (
              <div key={user._id} className="p-4 relative">
                <UserCard 
                  user={user}
                  showFollowButton
                  showMessageButton
                  onFollowChange={() => {}} // We'll handle follow changes in the parent component
                />
                <div className="absolute top-4 right-4">
                  <button
                    type="button"
                    onClick={(e) => handleUserActionClick(user, e)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <svg 
                      className="h-5 w-5" 
                      xmlns="http://www.w3.org/2000/svg" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" 
                      />
                    </svg>
                  </button>
                  
                  {/* User action menu */}
                  {showUserActionMenu && selectedUser && selectedUser._id === user._id && (
                    <div 
                      ref={userActionMenuRef}
                      className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                    >
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <Link
                          to={`/profile/${user.username}`}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          View Profile
                        </Link>
                        <Link
                          to={`/chat/${user._id}`}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          Send Message
                        </Link>
                        <button
                          type="button"
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          {user.isFollowing ? 'Unfollow' : 'Follow'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Post results */}
      {(activeType === '' || activeType === 'posts') && posts.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900 px-2">Posts</h2>
            {activeType === '' && posts.length > 3 && (
              <Link 
                to={`/search?q=${encodeURIComponent(query)}&type=posts`}
                className="text-sm font-medium text-blue-600 hover:text-blue-500 px-2"
              >
                View all
              </Link>
            )}
          </div>
          
          {posts.map((post) => (
            <PostCard key={post._id} post={post} />
          ))}
        </div>
      )}
      
      {/* Community results */}
      {(activeType === '' || activeType === 'communities') && communities.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900 px-2">Communities</h2>
            {activeType === '' && communities.length > 4 && (
              <Link 
                to={`/search?q=${encodeURIComponent(query)}&type=communities`}
                className="text-sm font-medium text-blue-600 hover:text-blue-500 px-2"
              >
                View all
              </Link>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {communities.map((community) => (
              <CommunityCard
                key={community._id}
                community={community}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

SearchResults.propTypes = {
  query: PropTypes.string,
  users: PropTypes.array,
  posts: PropTypes.array,
  communities: PropTypes.array,
  trendingTopics: PropTypes.array,
  trendingUsers: PropTypes.array,
  loading: PropTypes.bool,
  pagination: PropTypes.object,
  activeType: PropTypes.string,
  filters: PropTypes.object,
  onPageChange: PropTypes.func,
  onFilterChange: PropTypes.func,
};

export default SearchResults;